import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
// Filter controls + the detail drawer use the ui kit; the feed TABLE is hand-built with Tailwind.
import { Segmented, Descriptions, Input, Select, DatePicker, Button, Pagination, Drawer } from '../../ui';
import type { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../store/auth';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { fmtQty } from '../../lib/format';
import { disableFuture } from '../../lib/dateRange';
import { Pill } from '../../components/TwDataTable';

// Stock-ledger txn types → friendly labels.
const TXN_LABEL: Record<string, string> = {
  GRN: 'Received (GRN)', SO: 'Sales dispatch', TRANSFER_IN: 'Transfer in', TRANSFER_OUT: 'Transfer out',
  ADJUST_IN: 'Adjustment in', ADJUST_OUT: 'Adjustment out', RETURN_IN: 'Return in', OPENING: 'Opening stock',
};
const STATUS_COLOR: Record<string, string> = {
  ACCEPTED: 'lime', PACKED: 'gold', DISPATCHED: 'orange', DELIVERED: 'green', CANCELLED: 'red',
};
const label = (s: string) => (s ?? '').replace(/_/g, ' ');
const TH = 'px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-subtle';

/** Unified "what happened in my warehouse" feed for the Warehouse Manager — order fulfilment events
 *  (accept/pack/dispatch/deliver/cancel) merged with stock movements (GRN, transfers, adjustments,
 *  sales). Server-scoped to the caller's assigned warehouse(s); admins see all. */
export function WarehouseActivityPage({ embedded }: { embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const canStock = usePermission('STOCK.VIEW');
  const canGrn = usePermission('GRN.VIEW');
  const canWarehouses = usePermission('WAREHOUSE.VIEW');
  const [kind, setKind] = useState<'ALL' | 'ORDER' | 'STOCK'>('ALL');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | undefined>();   // order status OR stock txn type
  const [warehouseId, setWarehouseId] = useState<string | undefined>(); // admin only (a WM is auto-scoped)
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [detail, setDetail] = useState<any>(null); // a stock movement shown in the side drawer
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Where a movement's Reference links to — only when the viewer can actually open the target page.
  const refLink = (r: any): string | null => {
    if (r.orderId) return `/orders/${r.orderId}`;
    if (r.refTable === 'stock_transfers') return canStock ? '/inventory/master-stock?tab=transfers' : null;
    if (r.refTable === 'stock_adjustments') return canStock ? '/inventory/master-stock?tab=adjustments' : null;
    if (r.refTable === 'grn_headers') return canGrn ? '/inventory/grn' : null;
    return null;
  };
  const go = (to: string) => { setDetail(null); navigate(to); };
  // Stock delta as a soft tinted pill: light green for in, light red for out.
  const qtyDelta = (qty: number) => {
    const pos = qty >= 0;
    return (
      <span className={`inline-block rounded-pill px-2 text-xs font-semibold leading-5 ${pos ? 'bg-brand-500/12 text-brand-700 dark:text-brand-300' : 'bg-danger-500/12 text-danger-600 dark:text-danger-500'}`}>
        {pos ? '+' : '−'}{fmtQty(Math.abs(qty))}
      </span>
    );
  };

  // Date window + warehouse filter server-side (so older / other-warehouse history is fetched);
  // type, status/movement and free-text search narrow the fetched window client-side.
  const from = range[0] ? range[0].startOf('day').toISOString() : undefined;
  const to = range[1] ? range[1].endOf('day').toISOString() : undefined;
  const { data, isLoading } = useQuery({
    queryKey: ['warehouse-activity', warehouseId, from, to],
    queryFn: () => request<any[]>({ url: '/warehouse-activity', params: { warehouseId, from, to, limit: 250 } }),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
  // Warehouse picker options — admins only; a WM is locked to their own warehouse server-side.
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: canWarehouses,
    staleTime: 5 * 60_000,
  });

  // The status / movement dropdown is contextual to the Type segmented.
  const typeOptions = kind === 'ORDER'
    ? ['ACCEPTED', 'PACKED', 'DISPATCHED', 'DELIVERED', 'CANCELLED'].map((s) => ({ value: s, label: label(s) }))
    : kind === 'STOCK'
      ? Object.keys(TXN_LABEL).map((t) => ({ value: t, label: TXN_LABEL[t] }))
      : [];

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? [])
      .filter((r) => kind === 'ALL' || r.kind === kind)
      .filter((r) => !typeFilter || (r.kind === 'ORDER' ? r.toStatus === typeFilter : r.txnType === typeFilter))
      .filter((r) => !q || [r.orderNo, r.productName, r.sku, r.batchNo, r.ref, r.actorName, r.warehouseCode]
        .some((x) => String(x ?? '').toLowerCase().includes(q)));
  }, [data, kind, typeFilter, search]);

  const hasFilters = !!(search || typeFilter || warehouseId || range[0] || range[1] || kind !== 'ALL');
  const reset = () => { setSearch(''); setTypeFilter(undefined); setWarehouseId(undefined); setRange([null, null]); setKind('ALL'); setPage(1); };

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, pageCount);
  const paged = rows.slice((current - 1) * pageSize, current * pageSize);

  const activityCell = (r: any) => r.kind === 'ORDER' ? (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <b className="text-content">{r.orderNo}</b>
      {r.fromStatus && <Pill color={STATUS_COLOR[r.fromStatus] ?? 'default'}>{label(r.fromStatus)}</Pill>}
      {r.fromStatus && <span className="text-subtle">→</span>}
      <Pill color={STATUS_COLOR[r.toStatus] ?? 'blue'}>{label(r.toStatus)}</Pill>
      {r.note && <span className="text-xs text-muted">{r.note}</span>}
    </span>
  ) : (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="text-content">{r.productName ?? '—'}</span>
      {qtyDelta(r.qty)}
      <span className="text-xs text-muted">
        {TXN_LABEL[r.txnType] ?? r.txnType}{r.batchNo ? ` · ${r.batchNo}` : ''}{r.ref ? ` · ${r.ref}` : ''}
      </span>
    </span>
  );

  return (
    <div>
      {!embedded && <h2 className="mb-3 text-lg font-semibold text-content">Warehouse Activity</h2>}

      {/* Filter bar (Antd controls) */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented
          value={kind}
          onChange={(v) => { setKind(v as any); setTypeFilter(undefined); setPage(1); }}
          options={[{ label: 'All', value: 'ALL' }, { label: 'Orders', value: 'ORDER' }, { label: 'Stock', value: 'STOCK' }]}
        />
        {kind !== 'ALL' && (
          <Select
            allowClear placeholder={kind === 'ORDER' ? 'Any status' : 'Any movement'} style={{ width: 180 }}
            value={typeFilter} onChange={(v) => { setTypeFilter(v); setPage(1); }} options={typeOptions}
          />
        )}
        {canWarehouses && (
          <Select
            allowClear showSearch optionFilterProp="label" placeholder="All warehouses" style={{ width: 220 }}
            value={warehouseId} onChange={(v) => { setWarehouseId(v); setPage(1); }}
            options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), label: `${w.code} · ${w.name}`, isMaster: !!w.isMaster }))}
          />
        )}
        <DatePicker.RangePicker
          disabledDate={disableFuture} value={range as any}
          onChange={(r) => { setRange((r as any) ?? [null, null]); setPage(1); }} allowEmpty={[true, true]}
        />
        <Input
          placeholder="Search order / product / batch / ref" style={{ width: 240 }}
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        {hasFilters && <Button onClick={reset}>Reset</Button>}
      </div>

      {/* Feed table */}
      <div className="overflow-x-auto rounded-card border border-line bg-surface shadow-soft">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-surface-2">
            <tr>
              <th className={`${TH} w-44`}>When</th>
              <th className={`${TH} w-24`}>Type</th>
              <th className={TH}>Activity</th>
              <th className={`${TH} w-36`}>Warehouse</th>
              <th className={`${TH} w-40`}>By</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && !rows.length ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-t border-line"><td colSpan={5} className="px-3 py-3"><div className="h-6 animate-pulse rounded bg-surface-3" /></td></tr>
              ))
            ) : paged.length ? (
              paged.map((r: any) => (
                <tr
                  key={r.id}
                  className="cursor-pointer border-t border-line hover:bg-surface-2"
                  onClick={() => { if (r.kind === 'ORDER') { if (r.orderId) navigate(`/orders/${r.orderId}`); } else setDetail(r); }}
                >
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted">{formatIst(r.at)}</td>
                  <td className="px-3 py-2.5">{r.kind === 'ORDER' ? <Pill color="blue">Order</Pill> : <Pill color="purple">Stock</Pill>}</td>
                  <td className="px-3 py-2.5">{activityCell(r)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted">{r.warehouseCode ?? r.warehouseName ?? '—'}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-muted">{r.actorName ?? 'System'}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-subtle">No activity yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (
        <div className="mt-3 flex items-center justify-end">
          <Pagination
            size="small" current={current} pageSize={pageSize} total={rows.length}
            showSizeChanger onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
            showTotal={(t) => `${t} events`}
          />
        </div>
      )}

      <Drawer open={!!detail} onClose={() => setDetail(null)} width={440} title="Stock movement">
        {detail && (
          <Descriptions column={1} size="small" colon>
            <Descriptions.Item label="When">{formatIst(detail.at)}</Descriptions.Item>
            <Descriptions.Item label="Product">{detail.productName ?? '—'}{detail.sku ? ` · ${detail.sku}` : ''}</Descriptions.Item>
            <Descriptions.Item label="Batch">{detail.batchNo ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Movement">{TXN_LABEL[detail.txnType] ?? detail.txnType}</Descriptions.Item>
            <Descriptions.Item label="Quantity">{qtyDelta(detail.qty)}</Descriptions.Item>
            <Descriptions.Item label="Reference">
              {detail.ref
                ? (refLink(detail) ? <a onClick={() => go(refLink(detail)!)}>{detail.ref}</a> : detail.ref)
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Warehouse">
              {(detail.warehouseCode || detail.warehouseName)
                ? (canWarehouses
                    ? <a onClick={() => go('/inventory/warehouses')}>{detail.warehouseCode ?? detail.warehouseName}</a>
                    : (detail.warehouseCode ?? detail.warehouseName))
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="By">{detail.actorName ?? 'System'}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
}
